import React from 'react';
import {
  ArrowRight,
  BarChart3,
  Building2,
  ClipboardList,
  FileText,
  LayoutDashboard,
  LockKeyhole,
  ShieldCheck,
  Tags,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../lib/version';

const modules = [
  {
    title: 'Projects',
    description: 'Track project owners, timelines, status, and budget posture.',
    icon: ClipboardList,
  },
  {
    title: 'Budget Codes',
    description: 'Manage allocation categories, active codes, and planned limits.',
    icon: Tags,
  },
  {
    title: 'Budget Entries',
    description: 'Record income and expenses with project, unit, and date context.',
    icon: FileText,
  },
  {
    title: 'Reports & Admin',
    description: 'Review spending movement, users, settings, and exports.',
    icon: BarChart3,
  },
];

const HomePage: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const targetHref = isAuthenticated ? '/app' : '/login';

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-5 py-5 sm:px-8 lg:px-10">
          <header className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-700 text-white">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-5 text-slate-900">PCR Tracker</p>
                <p className="text-xs leading-4 text-slate-500">Sarawak Tourism Board</p>
              </div>
            </div>
            <a
              href={targetHref}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700"
            >
              {isAuthenticated ? 'Open Tracker' : 'Login'}
              <ArrowRight className="h-4 w-4" />
            </a>
          </header>

          <div className="grid flex-1 items-center gap-10 py-10 lg:grid-cols-[1fr_0.92fr] lg:py-12">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">
                <ShieldCheck className="h-3.5 w-3.5" />
                Production System
              </div>
              <h1 className="max-w-4xl text-4xl font-bold leading-tight text-slate-950 sm:text-5xl lg:text-6xl">
                PCR Project & Budget Tracker
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                Internal tracking system for project budgets, budget codes, spending entries,
                and reporting across authorised Sarawak Tourism teams.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={targetHref}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
                >
                  {loading ? 'Checking Session' : isAuthenticated ? 'Open Tracker' : 'Login to Tracker'}
                  <ArrowRight className="h-4 w-4" />
                </a>
                <div className="inline-flex h-12 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700">
                  <LockKeyhole className="h-4 w-4 text-slate-500" />
                  Authorised users only
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-950 p-4 shadow-2xl shadow-slate-200">
              <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
                <div>
                  <p className="text-sm font-semibold text-white">Budget Overview</p>
                  <p className="text-xs text-slate-400">Live project control surface</p>
                </div>
                <LayoutDashboard className="h-5 w-5 text-blue-300" />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {['Projects', 'Codes', 'Entries'].map((label, index) => (
                  <div key={label} className="rounded-lg bg-white/10 p-3">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="mt-2 text-2xl font-semibold text-white">{[12, 8, 34][index]}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-3">
                {['Event operations allocation', 'Digital campaign tracking', 'Vendor claim monitoring'].map((label, index) => (
                  <div key={label} className="rounded-lg bg-white p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-slate-900">{label}</p>
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                        {[72, 48, 31][index]}%
                      </span>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-blue-600"
                        style={{ width: `${[72, 48, 31][index]}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-3 pb-8 sm:grid-cols-2 lg:grid-cols-4">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <div key={module.title} className="rounded-lg border border-slate-200 bg-white p-4">
                  <Icon className="h-5 w-5 text-blue-700" />
                  <h2 className="mt-4 text-sm font-semibold text-slate-950">{module.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{module.description}</p>
                </div>
              );
            })}
          </div>

          <footer className="flex flex-col gap-2 border-t border-slate-200 py-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <span>© 2026 PCR Project & Budget Tracker</span>
            <span>Version {APP_VERSION} · Sarawak Tourism Board Internal Use</span>
          </footer>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
